import type { ApiMatrixSubQuestion, ApiQuestion } from '../../../../internalTypes';
import type { CfmMatrixStatementRow } from '../../../../types/cfmMatrix';
import { resolveSubQuestionText } from '../matrix/resolveStatementText';
import { mapAnswerLogic } from '../shared/mapAnswerLogic';

function resolveOppositeStatementText(
  sub: ApiMatrixSubQuestion | ApiQuestion,
  language?: string,
  isBipolar?: boolean
): string | undefined {
  const subQ = sub as ApiQuestion;
  if (isBipolar && subQ.answerDetails && subQ.answerDetails.length >= 2) {
    const rightText =
      (language && subQ.answerDetails[1].translations?.[language]?.answerText) ||
      subQ.answerDetails[1].answerText ||
      '';
    if (rightText) return rightText;
  }

  const matrixAdditional = (sub as ApiMatrixSubQuestion).additional;
  const text =
    (language && (sub.translations?.[language] as { description?: string })?.description) ||
    (sub as ApiQuestion).description ||
    matrixAdditional?.rightLabel ||
    matrixAdditional?.rightStatement ||
    matrixAdditional?.rightText;

  if (typeof text === 'string' && text.trim() !== '') {
    return text;
  }

  const rawText =
    (language && sub.translations?.[language]?.questionText) ||
    sub.questionText ||
    (sub as ApiMatrixSubQuestion).answerText ||
    '';

  if (isBipolar && rawText.includes(':')) {
    return rawText.split(':').slice(1).join(':').trim();
  }

  return undefined;
}

export function buildCfmStatementRows(
  question: ApiQuestion,
  language?: string,
  isBipolar?: boolean
): CfmMatrixStatementRow[] {
  const rowSrc =
    (question.matrixRowQuestions?.length ?? 0) > 0
      ? question.matrixRowQuestions
      : question.childSurveyQuestions;

  if (rowSrc && rowSrc.length > 0) {
    return rowSrc
      .filter(sub => sub.id)
      .map(sub => {
        const answerLogic = mapAnswerLogic(sub.controlConfig?.displayLogic);
        const oppositeStatementText = resolveOppositeStatementText(sub, language, isBipolar);
        return {
          id: sub.id!,
          statementText: resolveSubQuestionText(sub, language, isBipolar),
          ...(oppositeStatementText ? { oppositeStatementText } : {}),
          ...(answerLogic ? { answerLogic } : {}),
        };
      });
  }

  return (question.answerDetails ?? [])
    .filter(a => !a.hidden && !a.additional?.isHintText)
    .map((a, i) => ({
      id: `row_${i}`,
      statementText: (language && a.translations?.[language]?.answerText) || a.answerText || '',
    }));
}
